import { Temporal } from "@js-temporal/polyfill";

import type { FoodMacros, Meal } from "@/packages/dashboard/api/get-meals";
import { formatTimestamp } from "@/packages/dashboard/utils/format-timestamp";
import { getDisplayTimeZone } from "@/packages/dashboard/utils/timezone";
import { compareDatesDesc, temporalToDate } from "@/packages/dashboard/utils/date-range-helpers";

export type MacroTotals = {
  calories: number;
  proteins: number;
  carbs: number;
  fats: number;
};

export type MacroTargetValues = Partial<Record<keyof MacroTotals, number | null>>;

export const MACRO_FIELDS: Array<{ key: keyof MacroTotals; label: string }> = [
  { key: "calories", label: "Calories" },
  { key: "proteins", label: "Protein" },
  { key: "carbs", label: "Carbs" },
  { key: "fats", label: "Fat" },
];

export type MealWithTotals = {
  meal: Meal;
  totals: MacroTotals;
};

export type DayGroup = {
  key: string;
  label: string;
  date: Date;
  totals: MacroTotals;
  meals: MealWithTotals[];
};

export type WeekGroup = {
  key: string;
  label: string;
  startDate: Date;
  endDate: Date;
  totals: MacroTotals;
  mealCount: number;
  dayCount: number;
};

const macroFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

export const formatMacroValue = (value: number) =>
  macroFormatter.format(Number.isFinite(value) ? Math.round(value * 10) / 10 : 0);

export const createEmptyTotals = (): MacroTotals => ({
  calories: 0,
  proteins: 0,
  carbs: 0,
  fats: 0,
});

export const addTotals = (target: MacroTotals, addition: MacroTotals) => {
  target.calories += addition.calories;
  target.proteins += addition.proteins;
  target.carbs += addition.carbs;
  target.fats += addition.fats;
  return target;
};

const parseNumericValue = (value: string | number | null | undefined) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const parsed = parseFloat(value ?? "");
  return Number.isFinite(parsed) ? parsed : 0;
};

export const computeItemTotals = (quantity: string | number | null | undefined, foodMacros: FoodMacros): MacroTotals => {
  const qty = parseNumericValue(quantity);
  const servingSize = parseNumericValue(foodMacros.serving_size);
  
  if (qty === 0 || servingSize === 0) {
    return createEmptyTotals();
  }

  const macroPerUnit = {
    calories: parseNumericValue(foodMacros.calories) / servingSize,
    proteins: parseNumericValue(foodMacros.proteins) / servingSize,
    carbs: parseNumericValue(foodMacros.carbs) / servingSize,
    fats: parseNumericValue(foodMacros.fats) / servingSize,
  };

  return {
    calories: macroPerUnit.calories * qty,
    proteins: macroPerUnit.proteins * qty,
    carbs: macroPerUnit.carbs * qty,
    fats: macroPerUnit.fats * qty,
  };
};

export const buildMealTotals = (meal: Meal): MacroTotals => {
  const totals = createEmptyTotals();

  meal.meal_items.forEach((item) => {
    if (!item.food_item) return;
    addTotals(totals, computeItemTotals(item.quantity, item.food_item));
  });

  return totals;
};

const START_OF_DAY = Temporal.PlainTime.from("00:00");
const END_OF_DAY = Temporal.PlainTime.from("23:59:59.999999999");
const DAY_LABEL_FORMAT: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" };
const WEEK_LABEL_FORMAT: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };

const formatDayLabel = (date: Date) => formatTimestamp(date, { formatOptions: DAY_LABEL_FORMAT });

const formatWeekLabel = (start: Date, end: Date) => {
  const startLabel = formatTimestamp(start, { formatOptions: WEEK_LABEL_FORMAT });
  const endLabel = formatTimestamp(end, { formatOptions: WEEK_LABEL_FORMAT });

  if (start.getFullYear() === end.getFullYear()) {
    return `${startLabel} – ${endLabel}, ${start.getFullYear()}`;
  }

  return `${startLabel}, ${start.getFullYear()} – ${endLabel}, ${end.getFullYear()}`;
};

const getWeekStart = (dayStart: Temporal.ZonedDateTime) => {
  const daysFromMonday = (dayStart.dayOfWeek + 6) % 7;
  return dayStart.subtract({ days: daysFromMonday });
};

const getWeekEnd = (weekStart: Temporal.ZonedDateTime) => weekStart.add({ days: 6 }).withPlainTime(END_OF_DAY);

export const aggregateMeals = (meals: Meal[]) => {
  const timeZone = getDisplayTimeZone();
  const overallTotals = createEmptyTotals();
  const dayMap = new Map<string, DayGroup>();
  const weekMap = new Map<string, { group: Omit<WeekGroup, "dayCount">; dayKeys: Set<string> }>();

  meals.forEach((meal) => {
    const mealTotals = buildMealTotals(meal);
    addTotals(overallTotals, mealTotals);

    const zonedMealDate = Temporal.Instant.from(meal.timestamp).toZonedDateTimeISO(timeZone);
    const dayStart = zonedMealDate.withPlainTime(START_OF_DAY);
    const dayKey = dayStart.toPlainDate().toString();

    let dayGroup = dayMap.get(dayKey);
    if (!dayGroup) {
      const dayDate = temporalToDate(dayStart);
      dayGroup = {
        key: dayKey,
        label: formatDayLabel(dayDate),
        date: dayDate,
        totals: createEmptyTotals(),
        meals: [],
      };
      dayMap.set(dayKey, dayGroup);
    }

    addTotals(dayGroup.totals, mealTotals);
    dayGroup.meals.push({ meal, totals: mealTotals });

    const weekStartZdt = getWeekStart(dayStart);
    const weekKey = weekStartZdt.toPlainDate().toString();
    const weekEndZdt = getWeekEnd(weekStartZdt);

    let weekGroup = weekMap.get(weekKey);
    if (!weekGroup) {
      const weekStartDate = temporalToDate(weekStartZdt);
      const weekEndDate = temporalToDate(weekEndZdt);
      weekGroup = {
        group: {
          key: weekKey,
          label: formatWeekLabel(weekStartDate, weekEndDate),
          startDate: weekStartDate,
          endDate: weekEndDate,
          totals: createEmptyTotals(),
          mealCount: 0,
        },
        dayKeys: new Set<string>(),
      };
      weekMap.set(weekKey, weekGroup);
    }

    addTotals(weekGroup.group.totals, mealTotals);
    weekGroup.group.mealCount += 1;
    weekGroup.dayKeys.add(dayKey);
  });

  const dayGroups = Array.from(dayMap.values()).sort((a, b) => compareDatesDesc(a.date, b.date));
  dayGroups.forEach((day) => {
    day.meals.sort((a, b) => compareDatesDesc(new Date(a.meal.timestamp), new Date(b.meal.timestamp)));
  });

  const weekGroups: WeekGroup[] = Array.from(weekMap.values())
    .map(({ group, dayKeys }) => ({
      ...group,
      dayCount: dayKeys.size,
    }))
    .sort((a, b) => compareDatesDesc(a.startDate, b.startDate));

  return {
    overallTotals,
    dayGroups,
    weekGroups,
  };
};
